<template>
  <form novalidate class="pa-8" @submit.prevent="submit">
    <layout-section-header
      :title="$t('network_configuration')"
      :has-trigger="false"
    >
      <template #right-side>
        <Can action="see-has-api-checkbox" subject="clicky-networks">
          <v-checkbox
            v-model="hasApiIntegration"
            style="margin-top: 0; padding-top: 0"
            hide-details
          >
            <template #label>
              Has API integration
            </template>
          </v-checkbox>
        </Can>
      </template>
    </layout-section-header>

    <magic-container class="mt-4">
      <v-text-field
        v-model="name"
        :label="$t('name')"
        type="text"
        :error-messages="nameErrors"
        :hide-details="false"
      />
      <v-select
        v-model="networkType"
        :label="$t('traffic_network_type')"
        :items="networkTypes"
        item-title="name"
        item-value="id"
        :hide-details="false"
      />
    </magic-container>

    <v-textarea
      class="mt-4"
      v-model="trackingUrl"
      :label="$t('tracking_url')"
      auto-grow
      variant="outlined"
      density="compact"
      rows="1"
      :hide-details="false"
      @keydown="preventEnter"
    />

    <Can action="see-has-api-checkbox" subject="clicky-networks">
      <v-textarea
        class="mt-4"
        v-model="apiBaseUrl"
        :label="$t('api_base_url')"
        auto-grow
        variant="outlined"
        density="compact"
        rows="1"
        :hide-details="true"
        @keydown="preventEnter"
      />
    </Can>

    <div class="d-flex justify-end mt-6">
      <v-btn
        color="primary"
        type="submit"
        :loading="isLoading"
      >
        {{ $t('save') }}
      </v-btn>
    </div>
  </form>
</template>

<script>
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { isObj } from '@/utils';

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  props: {
    network: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      name: this.network.name,
      networkType: this.network.type?.id ?? null,
      trackingUrl: this.network.tracking_link,
      apiBaseUrl: this.network.api_base_url ?? '',
      hasApiIntegration: !!this.network.has_api,
      isLoading: false,
    };
  },
  validations: {
    name: { required },
  },
  created() {
    this.$store.dispatch('tracking/getTrafficNetworkTypes');
  },
  computed: {
    networkTypes() {
      return this.$store.getters['tracking/trafficNetworkTypes'];
    },
    nameErrors() {
      return this.v$.name.$errors.map(error => {
        if (error.$validator === 'required') return this.$t('required');
        return error.$message;
      });
    },
  },
  watch: {
    network: {
      handler(next) {
        if (isObj(next)) {
          this.name = next.name;
          this.networkType = next.type?.id ?? null;
          this.trackingUrl = next.tracking_link;
          this.apiBaseUrl = next.api_base_url ?? '';
          this.hasApiIntegration = !!next.has_api;
        }
      },
      deep: true,
    },
  },
  methods: {
    preventEnter(e) {
      if (e?.key === 'Enter') {
        e.preventDefault();
      }
    },
    submit() {
      if (this.isLoading) return;

      this.v$.$touch();
      if (this.v$.$invalid) return;
      this.isLoading = true;
      this.$store.dispatch('tracking/updateTrafficNetwork', {
        id: this.network.id,
        name: this.name,
        type: this.networkType,
        tracking_link: this.trackingUrl,
        ...(this.$ability.can('see-has-api-checkbox', 'clicky-networks') ? {
          api_base_url: this.apiBaseUrl,
          has_api: this.hasApiIntegration,
        } : {}),
      })
      .then(() => {
        this.v$.$reset();
      })
      .catch((err) => err)
      .finally(() => {
        this.isLoading = false;
      });
    },
  },
};
</script>

<style scoped>
.transition-width {
  transition: width 0.3s ease;
}
</style>
